import { FormEvent, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { getDeletionRequest, submitDeletionRequest, withdrawDeletionRequest, deletionRequestError } from "@/api/privacyRequests";
import { useAuthStore } from "@/stores/auth";
import { Card, SectionLabel } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { LoadError } from "@/components/ui/LoadError";

export function AccountDeletionRequest({ showExportLink = true }: { showExportLink?: boolean }) {
  const token = useAuthStore((s) => s.token);
  return <DeletionRequestForm key={token} session={token} showExportLink={showExportLink} />;
}

function DeletionRequestForm({ session, showExportLink }: { session: string | null; showExportLink: boolean }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const status = useQuery({ queryKey: ["account-deletion-request"], enabled: Boolean(session),
    queryFn: ({ signal }) => getDeletionRequest(session, signal) });
  const [password, setPassword] = useState("");
  const [acknowledged, setAcknowledged] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const active = useRef<AbortController | null>(null);
  useEffect(() => () => { active.current?.abort(); }, []);
  const pending = status.data?.request?.status === "requested";

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (active.current || !session || !password || !status.data || (!pending && !acknowledged)) return;
    const controller = new AbortController();
    active.current = controller;
    setBusy(true);
    setError(null);
    const verification = password;
    setPassword("");
    try {
      const result = pending && status.data.request
        ? await withdrawDeletionRequest(status.data.request.id, verification, session, controller.signal)
        : await submitDeletionRequest(verification, session, controller.signal);
      if (controller.signal.aborted || useAuthStore.getState().token !== session) return;
      queryClient.setQueryData(["account-deletion-request"], result);
      setAcknowledged(false);
    } catch (cause) {
      if (controller.signal.aborted || useAuthStore.getState().token !== session) return;
      setError(deletionRequestError(cause));
      // A lost response may still have committed. Read the durable status
      // instead of automatically replaying an operation with a stale request ID.
      void status.refetch();
    } finally {
      if (!controller.signal.aborted && useAuthStore.getState().token === session) {
        setPassword("");
        setBusy(false);
      }
      if (active.current === controller) active.current = null;
    }
  }

  return <Card>
    <SectionLabel>{t("account_deletion.title")}</SectionLabel>
    <p className="mt-2 text-sm text-taupe">{t("account_deletion.notice")}</p>
    <p className="mt-2 text-sm text-taupe">{t("account_deletion.controls")}</p>
    {showExportLink ? <Link to="/account/data" className="mt-2 inline-block text-sm text-amber-dark underline">{t("account_data.title")}</Link> : null}
    {status.isLoading ? <p role="status">{t("common.loading")}</p> : null}
    {status.isError ? <LoadError onRetry={() => void status.refetch()} retrying={status.isFetching} /> : null}
    {status.data ? <>
      {status.data.request ? <p className="my-3 text-sm" role="status">
        {t(`account_deletion.${status.data.request.status}`)} · #{status.data.request.id}
      </p> : null}
      <form className="mt-3 space-y-3" onSubmit={submit}>
        <Input type="password" label={t("account_deletion.password")} autoComplete="current-password"
          value={password} onChange={(event) => setPassword(event.target.value)} required maxLength={1024} disabled={busy} />
        {!pending ? <label className="flex items-start gap-2 text-sm">
          <input type="checkbox" checked={acknowledged} onChange={(event) => setAcknowledged(event.target.checked)} disabled={busy} />
          {t("account_deletion.acknowledge")}
        </label> : null}
        {error ? <p role="alert" className="text-sm text-coral">{t(`account_deletion.error_${error}`)}</p> : null}
        <Button type="submit" variant={pending ? "secondary" : "danger"} loading={busy}
          disabled={!password || (!pending && !acknowledged)}>
          {t(pending ? "account_deletion.withdraw" : "account_deletion.submit")}
        </Button>
      </form>
    </> : null}
  </Card>;
}
